/**
 * QA Session Context
 *
 * Global context for managing QA testing sessions.
 * Provides state management and event capture for the QA toolbar.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { getEffectiveAccountType } from '@/lib/account/accountTypes'
import type {
  QASession,
  QASessionState,
  QAEvent,
  QAScreenshot,
  QAEventType,
  QARiskLevel,
  QAUserType,
  QASessionConfig,
  QARecording,
} from '@/types/qa-session'
import { DEFAULT_SESSION_CONFIG, DEFAULT_RISK_LEVELS } from '@/types/qa-session'
import {
  generateQAId,
  getCurrentRoute,
  captureScreenshot,
  saveSessionToStorage,
  startScreenRecording,
  uploadRecording,
} from '@admin/lib/qa/utils'

interface QASessionContextType {
  // Session state
  session: QASession | null
  isRecording: boolean
  isPaused: boolean
  isScreenRecording: boolean

  // Session controls
  startSession: () => void
  pauseSession: () => void
  resumeSession: () => void
  endSession: () => void

  // Screen recording controls
  startRecording: () => Promise<void>
  stopRecording: () => Promise<void>

  // Event capture
  captureEvent: (
    type: QAEventType,
    message: string,
    details?: Record<string, any>,
    checklistItemId?: string,
  ) => void
  flagIssue: (message: string) => void
  addNote: (note: string) => void
  captureManualScreenshot: () => Promise<string | void>
  updateEventRiskLevel: (eventId: string, riskLevel: QARiskLevel) => void

  // Checklist integration
  setActiveChecklistItem: (itemId: string | null) => void
  updateChecklistItem: (itemId: string, status: string, notes?: string) => void

  // Configuration
  config: QASessionConfig
  updateConfig: (config: Partial<QASessionConfig>) => void
}

const QASessionContext = createContext<QASessionContextType | null>(null)

export const useQASession = () => {
  const context = useContext(QASessionContext)
  if (!context) {
    throw new Error('useQASession must be used within QASessionProvider')
  }
  return context
}

interface QASessionProviderProps {
  children: React.ReactNode
  enabled?: boolean // Feature flag
}

export const QASessionProvider: React.FC<QASessionProviderProps> = ({
  children,
  enabled = true,
}) => {
  const location = useLocation()
  const { accountData, accountId, user } = useAuth()

  const [session, setSession] = useState<QASession | null>(null)
  const [config, setConfig] = useState<QASessionConfig>(DEFAULT_SESSION_CONFIG)
  const [isScreenRecording, setIsScreenRecording] = useState(false)
  const routesVisitedRef = useRef<Set<string>>(new Set())
  const isCapturingScreenshot = useRef(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const stopRecordingRef = useRef<(() => Promise<Blob>) | null>(null)
  const currentRecordingIdRef = useRef<string | null>(null)

  // Determine current user type
  const userType: QAUserType = React.useMemo(() => {
    const accountType = getEffectiveAccountType(
      accountData as import('@/lib/account/accountTypes').AccountTypeData | null,
    )
    switch (accountType) {
      case 'admin':
        return 'admin'
      default:
        return 'individual'
    }
  }, [accountData])

  const isRecording = session?.state === 'recording'
  const isPaused = session?.state === 'paused'

  // Track route changes
  useEffect(() => {
    if (isRecording) {
      const route = getCurrentRoute()
      if (!routesVisitedRef.current.has(route)) {
        routesVisitedRef.current.add(route)
        captureEvent('route_change', `Navigated to ${route}`, {
          previousRoute: Array.from(routesVisitedRef.current)[routesVisitedRef.current.size - 2],
        })
      }
    }
  }, [location.pathname, location.search, isRecording])

  /**
   * Start a new QA session
   */
  const startSession = useCallback(() => {
    const sessionId = generateQAId('session')
    const now = Date.now()

    routesVisitedRef.current.clear()
    routesVisitedRef.current.add(getCurrentRoute())

    const newSession: QASession = {
      metadata: {
        sessionId,
        startTime: now,
        userType,
        accountId: accountId || undefined,
        accountEmail: user?.email || undefined,
        initialRoute: getCurrentRoute(),
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
      state: 'recording',
      events: [
        {
          id: generateQAId('event'),
          type: 'session_start',
          timestamp: now,
          route: getCurrentRoute(),
          userType,
          riskLevel: 'R3',
          message: 'QA Session started',
        },
      ],
      screenshots: [],
      recordings: [],
      routesVisited: [getCurrentRoute()],
      summary: {
        totalEvents: 1,
        errorsByRisk: { R0: 0, R1: 0, R2: 0, R3: 1 },
        eventsByType: {
          session_start: 1,
          console_error: 0,
          console_warn: 0,
          promise_rejection: 0,
          network_error: 0,
          network_timeout: 0,
          react_error: 0,
          permission_violation: 0,
          manual_flag: 0,
          manual_note: 0,
          screenshot: 0,
          route_change: 0,
          session_pause: 0,
          session_resume: 0,
          session_end: 0,
        },
        consoleErrors: 0,
        networkErrors: 0,
        permissionViolations: 0,
        manualFlags: 0,
      },
    }

    setSession(newSession)
  }, [userType, accountId, user?.email])

  /**
   * Pause the current session
   */
  const pauseSession = useCallback(() => {
    if (!session || session.state !== 'recording') return

    const event: QAEvent = {
      id: generateQAId('event'),
      type: 'session_pause',
      timestamp: Date.now(),
      route: getCurrentRoute(),
      userType,
      riskLevel: 'R3',
      message: 'Session paused',
    }

    setSession((prev) => {
      if (!prev) return null
      return {
        ...prev,
        state: 'paused',
        events: [...prev.events, event],
        summary: {
          ...prev.summary,
          totalEvents: prev.summary.totalEvents + 1,
          eventsByType: {
            ...prev.summary.eventsByType,
            session_pause: prev.summary.eventsByType.session_pause + 1,
          },
        },
      }
    })
  }, [session, userType])

  /**
   * Resume a paused session
   */
  const resumeSession = useCallback(() => {
    if (!session || session.state !== 'paused') return

    const event: QAEvent = {
      id: generateQAId('event'),
      type: 'session_resume',
      timestamp: Date.now(),
      route: getCurrentRoute(),
      userType,
      riskLevel: 'R3',
      message: 'Session resumed',
    }

    setSession((prev) => {
      if (!prev) return null
      return {
        ...prev,
        state: 'recording',
        events: [...prev.events, event],
        summary: {
          ...prev.summary,
          totalEvents: prev.summary.totalEvents + 1,
          eventsByType: {
            ...prev.summary.eventsByType,
            session_resume: prev.summary.eventsByType.session_resume + 1,
          },
        },
      }
    })
  }, [session, userType])

  /**
   * End the current session
   */
  const endSession = useCallback(() => {
    if (!session) return

    const now = Date.now()
    const duration = now - session.metadata.startTime

    const event: QAEvent = {
      id: generateQAId('event'),
      type: 'session_end',
      timestamp: now,
      route: getCurrentRoute(),
      userType,
      riskLevel: 'R3',
      message: 'Session ended',
    }

    const completedSession: QASession = {
      ...session,
      state: 'completed',
      metadata: {
        ...session.metadata,
        endTime: now,
        duration,
      },
      events: [...session.events, event],
      routesVisited: Array.from(routesVisitedRef.current),
      summary: {
        ...session.summary,
        totalEvents: session.summary.totalEvents + 1,
        eventsByType: {
          ...session.summary.eventsByType,
          session_end: session.summary.eventsByType.session_end + 1,
        },
      },
    }

    // Save to localStorage
    saveSessionToStorage(completedSession)

    setSession(completedSession)

    // Clear after a moment to show completion
    setTimeout(() => {
      setSession(null)
      routesVisitedRef.current.clear()
    }, 1000)
  }, [session, userType])

  /**
   * Capture a QA event
   */
  const captureEvent = useCallback(
    (
      type: QAEventType,
      message: string,
      details?: Record<string, any>,
      checklistItemId?: string,
    ) => {
      if (!session || session.state !== 'recording') return

      // Check event limit
      if (session.events.length >= config.maxEventsPerSession) {
        console.warn('QA Session: Max events reached, stopping capture')
        return
      }

      const riskLevel = DEFAULT_RISK_LEVELS[type]

      const event: QAEvent = {
        id: generateQAId('event'),
        type,
        timestamp: Date.now(),
        route: getCurrentRoute(),
        userType,
        riskLevel,
        message,
        details,
        checklistItemId: checklistItemId || session.checklist?.activeItem,
      }

      // Auto-capture screenshot for critical events
      const shouldAutoScreenshot =
        (config.autoScreenshotOnError &&
          (type === 'console_error' || type === 'react_error' || type === 'network_error')) ||
        (config.autoScreenshotOnPermissionViolation && type === 'permission_violation')

      if (shouldAutoScreenshot && !isCapturingScreenshot.current) {
        captureManualScreenshot().then((screenshotId) => {
          if (screenshotId) {
            event.screenshotId = screenshotId
          }
        })
      }

      setSession((prev) => {
        if (!prev) return null

        const updatedSummary = {
          ...prev.summary,
          totalEvents: prev.summary.totalEvents + 1,
          errorsByRisk: {
            ...prev.summary.errorsByRisk,
            [riskLevel]: prev.summary.errorsByRisk[riskLevel] + 1,
          },
          eventsByType: {
            ...prev.summary.eventsByType,
            [type]: prev.summary.eventsByType[type] + 1,
          },
          consoleErrors:
            type === 'console_error' ? prev.summary.consoleErrors + 1 : prev.summary.consoleErrors,
          networkErrors:
            type === 'network_error' || type === 'network_timeout'
              ? prev.summary.networkErrors + 1
              : prev.summary.networkErrors,
          permissionViolations:
            type === 'permission_violation'
              ? prev.summary.permissionViolations + 1
              : prev.summary.permissionViolations,
          manualFlags:
            type === 'manual_flag' ? prev.summary.manualFlags + 1 : prev.summary.manualFlags,
        }

        return {
          ...prev,
          events: [...prev.events, event],
          summary: updatedSummary,
        }
      })
    },
    [session, userType, config],
  )

  /**
   * Flag an issue manually
   */
  const flagIssue = useCallback(
    (message: string) => {
      captureEvent('manual_flag', message, { flagged: true })
    },
    [captureEvent],
  )

  /**
   * Add a manual note
   */
  const addNote = useCallback(
    (note: string) => {
      captureEvent('manual_note', note, { note })
    },
    [captureEvent],
  )

  /**
   * Capture screenshot manually
   */
  const captureManualScreenshot = useCallback(async (): Promise<string | void> => {
    if (!session || session.state !== 'recording') return

    if (isCapturingScreenshot.current) {
      console.warn('Screenshot capture already in progress')
      return
    }

    // Check screenshot limit
    if (session.screenshots.length >= config.maxScreenshotsPerSession) {
      console.warn('QA Session: Max screenshots reached')
      return
    }

    try {
      isCapturingScreenshot.current = true
      const dataUrl = await captureScreenshot()

      const screenshot: QAScreenshot = {
        id: generateQAId('screenshot'),
        timestamp: Date.now(),
        dataUrl,
        route: getCurrentRoute(),
        userType,
        triggeredBy: 'manual',
      }

      setSession((prev) => {
        if (!prev) return null
        return {
          ...prev,
          screenshots: [...prev.screenshots, screenshot],
          summary: {
            ...prev.summary,
            eventsByType: {
              ...prev.summary.eventsByType,
              screenshot: prev.summary.eventsByType.screenshot + 1,
            },
          },
        }
      })

      captureEvent('screenshot', 'Screenshot captured', { screenshotId: screenshot.id })

      return screenshot.id
    } catch (error) {
      console.error('Failed to capture screenshot:', error)
    } finally {
      isCapturingScreenshot.current = false
    }
  }, [session, userType, config, captureEvent])

  /**
   * Start screen recording
   */
  const startRecording = useCallback(async () => {
    if (!session || session.state !== 'recording') {
      console.warn('Cannot start recording: no active session')
      return
    }

    if (isScreenRecording) {
      console.warn('Screen recording already in progress')
      return
    }

    try {
      const { recorder, stopRecording } = await startScreenRecording()
      const recordingId = generateQAId('recording')

      mediaRecorderRef.current = recorder
      stopRecordingRef.current = stopRecording
      currentRecordingIdRef.current = recordingId
      setIsScreenRecording(true)

      // Add recording metadata to session
      const recording: QARecording = {
        id: recordingId,
        startTime: Date.now(),
        route: getCurrentRoute(),
        userType,
      }

      setSession((prev) => {
        if (!prev) return null
        return {
          ...prev,
          recordings: [...prev.recordings, recording],
        }
      })

      captureEvent('screenshot', 'Screen recording started', { recordingId })

      // Handle user stopping recording via browser UI
      recorder.onstop = async () => {
        if (isScreenRecording) {
          await stopRecording()
        }
      }
    } catch (error) {
      console.error('Failed to start screen recording:', error)
      setIsScreenRecording(false)
    }
  }, [session, isScreenRecording, userType, captureEvent])

  /**
   * Stop screen recording and upload to storage
   */
  const stopRecording = useCallback(async () => {
    if (!stopRecordingRef.current || !currentRecordingIdRef.current) {
      console.warn('No active recording to stop')
      return
    }

    try {
      const blob = await stopRecordingRef.current()
      const recordingId = currentRecordingIdRef.current

      // Update recording metadata with end time and file size
      setSession((prev) => {
        if (!prev) return null
        const recordings = prev.recordings.map((rec) => {
          if (rec.id === recordingId) {
            return {
              ...rec,
              endTime: Date.now(),
              duration: Date.now() - rec.startTime,
              fileSize: blob.size,
              mimeType: blob.type,
              blobUrl: URL.createObjectURL(blob),
            }
          }
          return rec
        })
        return { ...prev, recordings }
      })

      // Upload to Supabase storage
      if (session) {
        try {
          const storagePath = await uploadRecording(
            session.metadata.sessionId,
            recordingId,
            blob,
            'session_recording',
          )

          // Update recording with storage path
          setSession((prev) => {
            if (!prev) return null
            const recordings = prev.recordings.map((rec) => {
              if (rec.id === recordingId) {
                return { ...rec, storagePath }
              }
              return rec
            })
            return { ...prev, recordings }
          })

          captureEvent('screenshot', 'Screen recording saved', {
            recordingId,
            storagePath,
            fileSize: blob.size,
          })
        } catch (uploadError) {
          console.error('Failed to upload recording:', uploadError)
          captureEvent('console_error', 'Failed to upload screen recording', {
            error: uploadError instanceof Error ? uploadError.message : 'Unknown error',
          })
        }
      }

      setIsScreenRecording(false)
      mediaRecorderRef.current = null
      stopRecordingRef.current = null
      currentRecordingIdRef.current = null
    } catch (error) {
      console.error('Failed to stop screen recording:', error)
      setIsScreenRecording(false)
    }
  }, [session, captureEvent])

  /**
   * Update risk level of an event
   */
  const updateEventRiskLevel = useCallback((eventId: string, riskLevel: QARiskLevel) => {
    setSession((prev) => {
      if (!prev) return null

      const eventIndex = prev.events.findIndex((e) => e.id === eventId)
      if (eventIndex === -1) return prev

      const oldRiskLevel = prev.events[eventIndex].riskLevel
      const updatedEvents = [...prev.events]
      updatedEvents[eventIndex] = { ...updatedEvents[eventIndex], riskLevel }

      return {
        ...prev,
        events: updatedEvents,
        summary: {
          ...prev.summary,
          errorsByRisk: {
            ...prev.summary.errorsByRisk,
            [oldRiskLevel]: prev.summary.errorsByRisk[oldRiskLevel] - 1,
            [riskLevel]: prev.summary.errorsByRisk[riskLevel] + 1,
          },
        },
      }
    })
  }, [])

  /**
   * Update session configuration
   */
  const updateConfig = useCallback((newConfig: Partial<QASessionConfig>) => {
    setConfig((prev) => ({ ...prev, ...newConfig }))
  }, [])

  // Set up global error handlers when recording
  useEffect(() => {
    if (!enabled || !isRecording) return

    // Console error handler
    if (config.captureConsoleErrors) {
      const originalError = console.error
      console.error = (...args) => {
        captureEvent('console_error', args.map(String).join(' '), {
          errorMessage: args[0],
        })
        originalError.apply(console, args)
      }

      return () => {
        console.error = originalError
      }
    }
  }, [enabled, isRecording, config.captureConsoleErrors, captureEvent])

  useEffect(() => {
    if (!enabled || !isRecording) return

    // Console warn handler
    if (config.captureConsoleWarns) {
      const originalWarn = console.warn
      console.warn = (...args) => {
        captureEvent('console_warn', args.map(String).join(' '), {
          errorMessage: args[0],
        })
        originalWarn.apply(console, args)
      }

      return () => {
        console.warn = originalWarn
      }
    }
  }, [enabled, isRecording, config.captureConsoleWarns, captureEvent])

  useEffect(() => {
    if (!enabled || !isRecording) return

    // Promise rejection handler
    const handleRejection = (event: PromiseRejectionEvent) => {
      captureEvent('promise_rejection', event.reason?.message || 'Unhandled promise rejection', {
        reason: event.reason,
      })
    }

    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [enabled, isRecording, captureEvent])

  // Protect session from accidental page unload
  useEffect(() => {
    if (!session || session.state !== 'recording') return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Save session immediately before leaving
      const now = Date.now()
      const duration = now - session.metadata.startTime

      const completedSession: QASession = {
        ...session,
        state: 'completed',
        metadata: {
          ...session.metadata,
          endTime: now,
          duration,
        },
        routesVisited: Array.from(routesVisitedRef.current),
      }

      // Save to localStorage
      saveSessionToStorage(completedSession)

      // Show warning to user
      e.preventDefault()
      e.returnValue =
        'You have an active testing session. Your progress will be saved, but are you sure you want to leave?'
      return e.returnValue
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [session])

  // Network error interceptor would go here (using fetch/axios interceptors)
  // This is more complex and depends on your HTTP client setup

  /**
   * Set active checklist item for error association
   */
  const setActiveChecklistItem = useCallback((itemId: string | null) => {
    setSession((prev) => {
      if (!prev) return null
      return {
        ...prev,
        checklist: {
          ...prev.checklist,
          version: prev.checklist?.version || 'current',
          activeItem: itemId || undefined,
          items: prev.checklist?.items || {},
        },
      }
    })
  }, [])

  /**
   * Update checklist item status and notes
   */
  const updateChecklistItem = useCallback((itemId: string, status: string, notes?: string) => {
    setSession((prev) => {
      if (!prev) return null
      const items = { ...(prev.checklist?.items || {}) }
      items[itemId] = { status, notes, timestamp: Date.now() }

      return {
        ...prev,
        checklist: {
          ...prev.checklist,
          version: prev.checklist?.version || 'current',
          items,
        },
      }
    })
  }, [])

  const value: QASessionContextType = {
    session,
    isRecording,
    isPaused,
    isScreenRecording,
    startSession,
    pauseSession,
    resumeSession,
    endSession,
    startRecording,
    stopRecording,
    captureEvent,
    flagIssue,
    addNote,
    captureManualScreenshot,
    updateEventRiskLevel,
    setActiveChecklistItem,
    updateChecklistItem,
    config,
    updateConfig,
  }

  return <QASessionContext.Provider value={value}>{children}</QASessionContext.Provider>
}
